import { GetProjectionMatrix } from "../Math.js"

export default class Renderer {
    static #instanceCount = 0;
    #instanceId = ++Renderer.#instanceCount;

    constructor() {
        this.#InitOpenGL();
    }

    // Public // -----------------------------------------------------------------------------------------------------------
    Resize() {
        const canvas = this.#canvas;

        canvas.height = window.innerHeight;
        canvas.width = window.innerWidth;

        this.#opengl.viewport(0,
            0,
            canvas.width,
            canvas.height);

        var er = this.#opengl.getError();
        while (er != 0) {

            er = this.#opengl.getError();
        }

        this.#UpdateViewportTexture(canvas.width, canvas.height);
    }

    // ---------------------------------------------------------------------------------------------------------------------
    BindObject(object) {
        const mesh = object.GetMesh();

        var isLoaded = false;
        for (const m of this.#meshes) {
            if (m.GetId() == mesh.GetId()) {
                isLoaded = true;
                break;
            }
        }

        if (!isLoaded) {
            this.#LoadMesh(mesh);
        }

        this.#objects.push(object);
    }

    // ---------------------------------------------------------------------------------------------------------------------
    SetCamera(camera) { this.#camera = camera; }

    // ---------------------------------------------------------------------------------------------------------------------
    Render() {
        if (!this.#shaderProgram || !this.#textureProgram || !this.#camera) {
            return;
        }

        const gl = this.#opengl;
        const canvas = this.#canvas;
        const projection = GetProjectionMatrix(canvas.width, canvas.height)

        gl.bindFramebuffer(gl.FRAMEBUFFER, this.#frameBuffer);
        gl.viewport(0, 0, canvas.width, canvas.height);
        gl.enable(gl.DEPTH_TEST);

        gl.useProgram(this.#shaderProgram);

        gl.clearColor(0.039, 0.008, 0.059, 1);
        gl.clear(gl.COLOR_BUFFER_BIT | gl.DEPTH_BUFFER_BIT);

        this.#LoadProjection(projection);
        this.#LoadRes([this.#canvas.width, this.#canvas.height]);
        this.#LoadCamera(this.#camera.GetTransformMat());
        this.#LoadCameraPos(this.#camera.GetPos());

        var renders = [
            gl.LINE_STRIP,
            gl.POINTS,
            gl.POINTS,
            gl.TRIANGLES,
            gl.LINE_LOOP,
            gl.POINTS,
            gl.POINTS,
            gl.POINTS,
            gl.LINE_LOOP,
            gl.POINTS,
            gl.LINE_LOOP,
            gl.POINTS,
            gl.POINTS,
        ];
        try {
            var i = 0;
            for (const obj of this.#objects) {
                this.#LoadForRender(obj.GetMesh());
                this.#LoadTransformMat(obj.GetTransformMat());

                gl.drawElements(renders[i], obj.GetMesh().GetIndexAmount(), gl.UNSIGNED_SHORT, 0);
                i = (i + 1) % renders.length;
            }

        } catch (error) {
            console.error('Renderer: error while drawing scene objects', error);
        }

        gl.useProgram(this.#textureProgram);
        const loc = gl.getUniformLocation(this.#textureProgram, 'iResolution');

        var w = Math.min(Math.max(this.#canvas.width, this.#canvas.height), 1280) * .5;
        var h = Math.min(Math.min(this.#canvas.width, this.#canvas.height), 768) * .5;

        gl.uniform2f(loc, w, h);

        gl.bindFramebuffer(gl.FRAMEBUFFER, null); // draw to the default framebuffer (the canvas), not back into itself
        gl.viewport(0, 0, canvas.width, canvas.height);
        gl.disable(gl.DEPTH_TEST);
        gl.clearColor(0, 0, 0, 1);
        gl.clear(gl.COLOR_BUFFER_BIT);

        if (this.#quadPositionLoc === -1) {
            console.error('Renderer: a_position not found on textureProgram (quadPositionLoc === -1)');
        }

        gl.bindBuffer(gl.ARRAY_BUFFER, this.#quadVertexBuffer);
        gl.enableVertexAttribArray(this.#quadPositionLoc);
        gl.vertexAttribPointer(this.#quadPositionLoc, 3, gl.FLOAT, false, 0, 0);

        gl.activeTexture(gl.TEXTURE0);
        gl.bindTexture(gl.TEXTURE_2D, this.#viewportTexture);
        gl.uniform1i(gl.getUniformLocation(this.#textureProgram, 'renderedTexture'), 0);
        gl.uniform1f(gl.getUniformLocation(this.#textureProgram, 'time'), (performance.now() - this.#startTime) / 1000);

        gl.drawArrays(gl.TRIANGLES, 0, 6);

        const err = gl.getError();
        if (err !== gl.NO_ERROR) {
            console.error('Renderer: GL error during post-process pass:', err.toString(16));
        }
    }

    // ---------------------------------------------------------------------------------------------------------------------
    Dispose() {
        const gl = this.#opengl;
        if (!gl) return;

        for (const mesh of this.#meshes) {
            gl.deleteBuffer(mesh.GetGPUVertexBuffer());
            gl.deleteBuffer(mesh.GetGPUIndexBuffer());
            gl.deleteBuffer(mesh.GetGPUNormalBuffer());
        }
        this.#meshes = [];
        this.#objects = [];

        if (this.#quadVertexBuffer) gl.deleteBuffer(this.#quadVertexBuffer);
        if (this.#frameBuffer) gl.deleteFramebuffer(this.#frameBuffer);
        if (this.#viewportTexture) gl.deleteTexture(this.#viewportTexture);
        if (this.#depthBuffer) gl.deleteRenderbuffer(this.#depthBuffer);
        if (this.#shaderProgram) gl.deleteProgram(this.#shaderProgram);
        if (this.#textureProgram) gl.deleteProgram(this.#textureProgram);

        this.#shaderProgram = null;
        this.#textureProgram = null;
    }

    // Private // ----------------------------------------------------------------------------------------------------------
    #InitOpenGL() {
        this.#CreateContext();

        var er = this.#opengl.getError();
        while (er != 0) {

            er = this.#opengl.getError();
        }
        this.Resize();
        this.#LoadPipeline();
    }

    // ---------------------------------------------------------------------------------------------------------------------
    #CreateContext() {
        this.#canvas = document.getElementById('BannerCanvas');
        this.#opengl = this.#canvas.getContext('webgl2');
        this.#objects = [];
        this.#meshes = [];

        var er = this.#opengl.getError();
        while (er != 0) {

            er = this.#opengl.getError();
        }

        if (!this.#opengl) {
            alert('WebGL2 not supported');
            throw new Error('WebGL not supported');
        }
    }

    // ---------------------------------------------------------------------------------------------------------------------
    async #LoadPipeline() {
        const gl = this.#opengl;

        gl.enable(gl.CULL_FACE);
        gl.enable(gl.DEPTH_TEST);
        gl.depthFunc(gl.LEQUAL);

        this.#shaderProgram = await this.#LoadShaders();
        this.#textureProgram = await this.#LoadShadersTexture();

        this.#InitQuad();
        this.#startTime = performance.now();
    }

    // ---------------------------------------------------------------------------------------------------------------------
    #InitQuad() {
        const gl = this.#opengl;

        var g_quad_vertex_buffer_data = new Float32Array([
            -1.0, -1.0, 0.0,
            1.0, -1.0, 0.0,
            -1.0, 1.0, 0.0,
            -1.0, 1.0, 0.0,
            1.0, -1.0, 0.0,
            1.0, 1.0, 0.0,
        ]);

        this.#quadVertexBuffer = gl.createBuffer();
        gl.bindBuffer(gl.ARRAY_BUFFER, this.#quadVertexBuffer);
        gl.bufferData(gl.ARRAY_BUFFER, g_quad_vertex_buffer_data, gl.STATIC_DRAW);

        this.#quadPositionLoc = gl.getAttribLocation(this.#textureProgram, 'a_position');
    }

    // ---------------------------------------------------------------------------------------------------------------------
    async #LoadShaders() {
        const fragmentSource = '#version 300 es\n             precision mediump float;\n             \n             layout(location = 0) out vec3 color; \n             \n             in vec3 pos;\n             in vec3 Normal;\n             in vec3 Camera;\n             uniform vec2 u_resolution;\n             \n             void main()\n             {\n             vec2 st = gl_FragCoord.xy/u_resolution.xy; \n             vec3 lightPos   = vec3(-500.2,  -400.0, -800.0);\n             vec3 lightColor = vec3(1.0, 1.0, 1.0);\n             vec3 viewPos    = Camera;\n             \n             float ambientStrength  = .6;\n             float diffuseStrength  = 1.8;\n             float specularStrength = 4.8;\n             float shininess        = 520.0;\n             \n             vec3 norm = normalize(Normal);\n             vec3 lightDir = normalize(lightPos - pos);\n             vec3 viewDir = normalize(viewPos - pos);\n             vec3 reflectDir = reflect(-lightDir, norm);\n             \n             // Ambient\n             vec3 ambient = ambientStrength * lightColor;\n             \n             // Diffuse\n             float diff = max(dot(norm, lightDir), 0.0);\n             vec3 diffuse = diffuseStrength * diff * lightColor;\n             \n             // Specular\n             float spec = pow(max(dot(viewDir, reflectDir), 0.0), shininess);\n             vec3 specular = specularStrength * spec * lightColor;\n             \n             vec3 baseColor = vec3(mix(.5, mix(0.2 + clamp(sin(st.x * 2.129308) - cos(st.y * 19.98023), 0.0, 0.8), .5, 0.1), 0.4) , \n                              mix(.5, mix(0.2 + clamp(sin(st.y * 2.129308) + cos(st.y * 19.98023), 0.0, 0.8), .5, 0.1), 0.4) , \n                              mix(.8, mix(0.4 + clamp(sin(st.y * 2.129308) + cos(st.x * 19.98023), 0.0, 0.8), .5, 0.1), 0.4) ); // base color\n             vec3 result = (ambient + diffuse + specular) * baseColor;\n             \n             color = result;\n             }\n             \n             ';
        const vertexSource = '#version 300 es\n             precision mediump float;\n             \n             in vec4 a_position;\n             in vec3 a_normal;\n             uniform mat4 u_transform;\n             uniform mat4 u_camera_view;\n             uniform vec3 u_camera_pos;\n             uniform mat4 u_project;\n             out vec3 pos;\n             out vec3 Normal;\n             out vec3 Camera;\n             \n             void main()\n             {\n             vec4 worldPos   = u_transform * a_position;\n             gl_Position     = u_project * u_camera_view * worldPos;\n             \n             mat3 normalMatrix = mat3(transpose(inverse(u_transform)));\n             \n             Normal  = normalize(normalMatrix * a_normal);\n             Camera  = u_camera_pos;\n             pos     = vec3(u_transform * a_position);\n             }\n             \n             ';

        return this.#CreateProgram(vertexSource,
            fragmentSource);
    }

    // ---------------------------------------------------------------------------------------------------------------------
    async #LoadShaderSource(url) {
        const response = await fetch(url);
        if (!response.ok) {
            throw new Error(`Failed to load shader: ${url}`);
        }
        return response.text();
    }

    // ---------------------------------------------------------------------------------------------------------------------
    #CreateShader(type, source) {
        const gl = this.#opengl;
        const shader = gl.createShader(type);

        gl.shaderSource(shader, source);
        gl.compileShader(shader);

        if (!gl.getShaderParameter(shader, gl.COMPILE_STATUS)) {
            const err = gl.getShaderInfoLog(shader);
            gl.deleteShader(shader);

            throw new Error('Shader compile error: ' + err);
        }

        return shader;
    }

    // ---------------------------------------------------------------------------------------------------------------------
    #CreateProgram(vertexSrc, fragmentSrc) {
        const gl = this.#opengl;
        const vertexShader = this.#CreateShader(gl.VERTEX_SHADER, vertexSrc);
        const fragmentShader = this.#CreateShader(gl.FRAGMENT_SHADER, fragmentSrc);
        const program = gl.createProgram();

        gl.attachShader(program, vertexShader);
        gl.attachShader(program, fragmentShader);
        gl.linkProgram(program);

        if (!gl.getProgramParameter(program, gl.LINK_STATUS)) {
            const err = gl.getProgramInfoLog(program);
            gl.deleteProgram(program);

            throw new Error('Program link error: ' + err);
        }

        return program;
    }

    // ---------------------------------------------------------------------------------------------------------------------
    #LoadBuffers(mesh) {
        const gl = this.#opengl;
        const positionBuffer = gl.createBuffer();
        const indexBuffer = gl.createBuffer();
        const normalBuffer = gl.createBuffer();
        const vertices = mesh.GetVertices();
        const indexes = mesh.GetFaces();
        const normals = mesh.GetNormals();

        gl.bindBuffer(gl.ARRAY_BUFFER, positionBuffer);
        gl.bufferData(gl.ARRAY_BUFFER, vertices, gl.STATIC_DRAW);

        gl.bindBuffer(gl.ELEMENT_ARRAY_BUFFER, indexBuffer);
        gl.bufferData(gl.ELEMENT_ARRAY_BUFFER, indexes, gl.STATIC_DRAW);

        gl.bindBuffer(gl.ARRAY_BUFFER, normalBuffer);
        gl.bufferData(gl.ARRAY_BUFFER, normals, gl.STATIC_DRAW);

        mesh.SetGPUVertexBuffer(positionBuffer);
        mesh.SetGPUIndexBuffer(indexBuffer);
        mesh.SetGPUNormalBuffer(normalBuffer);
    }

    // ---------------------------------------------------------------------------------------------------------------------
    #LoadForRender(mesh) {
        const gl = this.#opengl;
        const positionAttribLocation = gl.getAttribLocation(this.#shaderProgram, 'a_position');
        const normalAttribLocation = gl.getAttribLocation(this.#shaderProgram, 'a_normal');
        const gpuVertexBuffer = mesh.GetGPUVertexBuffer();
        const gpuIndexBuffer = mesh.GetGPUIndexBuffer();
        const gpuNormalBuffer = mesh.GetGPUNormalBuffer();

        gl.bindBuffer(gl.ARRAY_BUFFER, gpuVertexBuffer);
        gl.enableVertexAttribArray(positionAttribLocation);
        gl.vertexAttribPointer(positionAttribLocation, 3, gl.FLOAT, false, 0, 0);

        gl.bindBuffer(gl.ELEMENT_ARRAY_BUFFER, gpuIndexBuffer);

        gl.bindBuffer(gl.ARRAY_BUFFER, gpuNormalBuffer);
        gl.enableVertexAttribArray(normalAttribLocation);
        gl.vertexAttribPointer(normalAttribLocation, 3, gl.FLOAT, false, 0, 0);

    }
    // ---------------------------------------------------------------------------------------------------------------------
    #LoadUniformMat(matrix, what) {
        const gl = this.#opengl;
        const program = this.#shaderProgram;
        const loc = gl.getUniformLocation(program, what);

        gl.uniformMatrix4fv(loc, false, matrix);
    }

    // ---------------------------------------------------------------------------------------------------------------------
    #LoadMesh(mesh) {
        this.#LoadBuffers(mesh);
        this.#meshes.push(mesh);
    }

    // ---------------------------------------------------------------------------------------------------------------------
    #LoadTransformMat(mat) { this.#LoadUniformMat(mat, "u_transform"); }

    // ---------------------------------------------------------------------------------------------------------------------
    #LoadCamera(mat) { this.#LoadUniformMat(mat, "u_camera_view"); }

    // ---------------------------------------------------------------------------------------------------------------------
    #LoadProjection(mat) { this.#LoadUniformMat(mat, "u_project"); }

    // ---------------------------------------------------------------------------------------------------------------------
    #LoadCameraPos(vec) {
        const gl = this.#opengl;
        const program = this.#shaderProgram;
        const loc = gl.getUniformLocation(program, "u_camera_pos");

        gl.uniform3fv(loc, vec);
    }

    // ---------------------------------------------------------------------------------------------------------------------
    #LoadRes(vec) {
        const gl = this.#opengl;
        const program = this.#shaderProgram;
        const loc = gl.getUniformLocation(program, "u_resolution");

        gl.uniform2fv(loc, vec);
    }

    // ----------------------------------------------------------------------------------------------------------------
    #UpdateViewportTexture(width, height) {
        const gl = this.#opengl;
        if (this.#frameBuffer != null) {
            gl.deleteFramebuffer(this.#frameBuffer);
        }
        this.#frameBuffer = gl.createFramebuffer();
        gl.bindFramebuffer(gl.FRAMEBUFFER, this.#frameBuffer);

        if (this.#viewportTexture != null) {
            gl.deleteTexture(this.#viewportTexture);
        }
        this.#viewportTexture = gl.createTexture();
        gl.bindTexture(gl.TEXTURE_2D, this.#viewportTexture);
        gl.texImage2D(gl.TEXTURE_2D, 0, gl.RGB, width, height, 0, gl.RGB, gl.UNSIGNED_BYTE, null);
        gl.texParameteri(gl.TEXTURE_2D, gl.TEXTURE_MAG_FILTER, gl.NEAREST);
        gl.texParameteri(gl.TEXTURE_2D, gl.TEXTURE_MIN_FILTER, gl.NEAREST);

        if (this.#depthBuffer != null) {
            gl.deleteRenderbuffer(this.#depthBuffer);
        }
        this.#depthBuffer = gl.createRenderbuffer();
        gl.bindRenderbuffer(gl.RENDERBUFFER, this.#depthBuffer);
        gl.renderbufferStorage(gl.RENDERBUFFER, gl.DEPTH_COMPONENT16, width, height);
        gl.framebufferRenderbuffer(gl.FRAMEBUFFER, gl.DEPTH_ATTACHMENT, gl.RENDERBUFFER, this.#depthBuffer);

        gl.framebufferTexture2D(gl.FRAMEBUFFER, gl.COLOR_ATTACHMENT0, gl.TEXTURE_2D, this.#viewportTexture, 0);
        this.#drawBuffers = [gl.COLOR_ATTACHMENT0];
        gl.drawBuffers(this.#drawBuffers);

        const status = gl.checkFramebufferStatus(gl.FRAMEBUFFER);
        if (status !== gl.FRAMEBUFFER_COMPLETE) {
            console.error('Renderer: offscreen framebuffer is INCOMPLETE, status =', status.toString(16));
        }
    }

    // --------------------------------------------------------------------------------------------------------------------
    async #LoadShadersTexture() {
        // const fragmentSource = '#version 300 es\n        //     precision mediump float;\n         //     \n        //     in vec2 UV; \n        //     \n        // \n        //     uniform sampler2D renderedTexture; \n        //     uniform float time; \n        //     out vec4 FragColor; \n        // \n        //     void main(){ \n        //     vec3 color = texture(renderedTexture, UV).xyz; \n        //     FragColor = vec4(color, 1.0); \n        // } \n        // ';
        const fragmentSource = '#version 300 es\n            precision mediump float;\n             #define CURVATURE 1.\n            #define SCANLINES 1.\n            #define CURVED_SCANLINES 1.\n            #define BLURED 1.\n            #define LIGHT 1.\n            #define COLOR_CORRECTION 1.\n            // #define ASPECT_RATIO 1.\n            //\n            const float gamma = 0.8;\n            const float contrast = 1.;\n            const float saturation = 2.;\n            const float brightness = 1.;\n            \n            const float light = 15.;\n            const float blur = .5;\n            in vec2 fragCoord;\n             out vec4 FragColor; \n            uniform vec2 iResolution;\n             uniform sampler2D iChannel0; \n            \n            vec3 postEffects(in vec3 rgb, in vec2 xy) {\n            rgb = pow(rgb, vec3(gamma));\n            rgb = mix(vec3(.5), mix(vec3(dot(vec3(.2125, .7154, .0721), rgb*brightness)), rgb*brightness, saturation), contrast);\n            \n            return rgb;\n            }\n            \n            // Sigma 1. Size 3\n            vec3 gaussian(in vec2 uv) {\n            float b = blur / (iResolution.x / iResolution.y);\n            \n            uv+= .5;\n            \n     vec3 col = texture(iChannel0, vec2(uv.x - b/iResolution.x, uv.y - b/iResolution.y) ).rgb * 0.077847;\n            col += texture(iChannel0, vec2(uv.x - b/iResolution.x, uv.y) ).rgb * 0.123317;\n            col += texture(iChannel0, vec2(uv.x - b/iResolution.x, uv.y + b/iResolution.y) ).rgb * 0.077847;\n            \n            col += texture(iChannel0, vec2(uv.x, uv.y - b/iResolution.y) ).rgb * 0.123317;\n            col += texture(iChannel0, vec2(uv.x, uv.y) ).rgb * 0.195346;\n            col += texture(iChannel0, vec2(uv.x, uv.y + b/iResolution.y) ).rgb * 0.123317;\n            \n            col += texture(iChannel0, vec2(uv.x + b/iResolution.x, uv.y - b/iResolution.y) ).rgb * 0.077847;\n            col += texture(iChannel0, vec2(uv.x + b/iResolution.x, uv.y) ).rgb * 0.123317;\n            col += texture(iChannel0, vec2(uv.x + b/iResolution.x, uv.y + b/iResolution.y) ).rgb * 0.077847;\n            \n            return col;\n            }\n            \n            void main()\n            {\n            vec2 st = fragCoord - 0.5;\n            \n            // Curvature/light\n             float d = length(st*.5 * st*.5);\n            #ifdef CURVATURE\n            vec2 uv = st*d + st*.935;\n            #else\n            vec2 uv = st;\n            #endif\n            \n            // Fudge aspect ratio\n             #ifdef ASPECT_RATIO\n             uv.x *= iResolution.x/iResolution.y*.75;\n             #endif\n            \n            // CRT color blur\n             #ifdef BLURED\n            vec3 color = gaussian(uv);\n            #else\n            vec3 color = texture(iChannel0, uv+.5).rgb;\n            #endif\n            \n            // Light\n            #ifdef LIGHT\n            float l = 1. - min(1., d*light);\n            color *= l;\n            #endif\n            \n            // Scanlines\n            #ifdef CURVED_SCANLINES\n            float y = uv.y;\n            #else\n            float y = st.y;\n            #endif\n            \n            float showScanlines = 1.;\n            if (iResolution.y<360.) showScanlines = 0.;\n            \n            #ifdef SCANLINES\n            float s = 1. - smoothstep(320., 1440., iResolution.y) + 1.;\n            float j = cos(y*iResolution.y*s)*.1; // values between .01 to .25 are ok.\n            color = abs(showScanlines-1.)*color + showScanlines*(color - color*j);\n            color *= 1. - ( .01 + ceil(mod( (st.x+.5)*iResolution.x, 3.) ) * (.995-1.01) )*showScanlines;\n            #endif\n            \n            // Border mask\n             #ifdef CURVATURE\n             float m = max(0.0, 1. - 2.*max(abs(uv.x), abs(uv.y) ) );\n             m = min(m*200., 1.);\n             color *= m;\n             #endif\n             \n             // Color correction\n             #ifdef COLOR_CORRECTION\n             color = postEffects(color, st);\n             #endif\n             \n             FragColor = vec4(color, 1.);\n             }';

        const vertexSource = '#version 300 es\n             precision mediump float;\n             \n             in vec3 a_position;\n             out vec2 fragCoord;\n             \n             void main()\n             {\n             gl_Position = vec4(a_position, 1.0);\n             fragCoord = a_position.xy * 0.5 + 0.5;\n             }\n             \n             ';

        return this.#CreateProgram(vertexSource,
            fragmentSource);
    }

    // ---------------------------------------------------------------------------------------------------------------------
    /** @type { HTMLCanvasElement } */
    #canvas;
    /** @type { WebGLRenderingContext } */
    #opengl;

    #shaderProgram;
    #textureProgram;

    #frameBuffer;
    #viewportTexture;
    #depthBuffer;
    #drawBuffers;

    #quadVertexBuffer;
    #quadPositionLoc;
    #startTime;

    #camera;
    #objects = [];
    #meshes = [];
};

